import { createContext, useState } from "react";
import { jwtDecode } from "jwt-decode";
import PropTypes from "prop-types";
import { clearAllDrafts } from "../components/training/lib/draft";

const AuthContext = createContext(null);

// Read and validate the stored JWT synchronously so the very first render
// already reflects the correct auth state. This matters for routing guards
// (RequireAuth / PublicOnly): if isLoggedIn were async, a hard refresh of a
// protected deep link would bounce through /login → DEFAULT_ROUTE before
// the token check completed, losing the original URL.
//
// A token dropped here is a sign-out too — the most common one: tokens last a
// day and there is no refresh, so reopening the app the next day lands here,
// not in logout(). It forgets Training's plan drafts the same way (c6).
function dropToken() {
  localStorage.removeItem("access_token");
  clearAllDrafts();
}

function readInitialAuth() {
  const token = localStorage.getItem("access_token");
  if (!token) {
    return { isLoggedIn: false, user: null };
  }
  try {
    const decodedToken = jwtDecode(token);
    if (decodedToken.exp * 1000 > Date.now()) {
      return { isLoggedIn: true, user: decodedToken };
    }
    // Expired — drop it.
    dropToken();
    return { isLoggedIn: false, user: null };
  } catch (error) {
    // Malformed — drop it.
    dropToken();
    console.error(error);
    return { isLoggedIn: false, user: null };
  }
}

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(readInitialAuth);

  const login = (token, userData = null) => {
    localStorage.setItem("access_token", token);
    const decodedToken = jwtDecode(token);
    setAuth({
      isLoggedIn: true,
      user: userData ? { ...decodedToken, ...userData } : decodedToken,
    });
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    // Training's plan drafts hold the check-in (sleep, stress, pain): they
    // go with the sign-in, every person's (c6). Its set queue stays on
    // purpose — unsent sets must survive until they are sent, and a 401
    // lands here mid-session — see clearAllDrafts.
    clearAllDrafts();
    setAuth({ isLoggedIn: false, user: null });
  };

  const contextValue = {
    isLoggedIn: auth.isLoggedIn,
    user: auth.user,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
